import { Box, Divider, Theme } from '@mui/material';
import { CancelIcon, SelectAllIcon, TrashIcon } from '@synergy/theme';
import { ConfirmationDialog, PoolButton, PoolFormattedMessage, PoolTypography } from '@synergy/components';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { ImportItemStatus, ImportStatus } from '@synergy/models';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { PaloActivityDock } from '@synergy/components-palo';
import { PoolDocumentTitle, RouteTab, RouteTabs } from '@synergy/components-next';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material';

import { ImportDetailErrorTable } from './import-detail-error-table';
import { ImportDetailFilter } from '../../components/import-detail-filter';
import { ImportDetailSidebar } from './import-detail-sidebar';
import { ImportDetailTable } from './import-detail-table';
import { ImportDetailTabs } from '../../models/import-detail-tabs';
import { ImportStatusChip } from '../../components/import-status-chip';
import { PaloOverview } from '../../../../../common/components/palo/palo-overview';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import {
    abortImport,
    deleteImport,
    fetchImportDetails,
    fetchImportJobItems,
    publishAllImports,
    publishArticles,
    removeArticle as removeArticles,
} from '../../store/all/import-actions';
import { isImportDeletable } from '../../utils/is-import-deletable';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    spinner: {
        width: '100%',
    },
    container: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.default,
    },
    statusContainer: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.common.white,
    },
    statusChip: {
        margin: '0 16px',
        height: '1.7rem',
    },
    tabContainer: {
        '& div:first-child': {
            margin: 0,
            boxShadow: 'none',
        },
    },
    tabs: {
        marginLeft: theme.general.pageContentSpacing,
    },
    contentContainer: {
        margin: 0,
        padding: '20px 57px 0px 50px',
        flexGrow: 1,
    },
    negative: {
        color: '#f44336',
    },
    footer: {
        height: theme.spacing(11),
        padding: '24px 70px',
        display: 'flex',
        flexDirection: 'row-reverse',
        backgroundColor: '#ffffff',
    },
    filterModal: {
        maxWidth: 560,
    },
}));

enum ImportDeleteArticlePublishStatus {
    None,
    All,
    Single,
}

export const ImportDetail = () => {
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const { id: importJobId } = useParams();
    const classes = useStyles();
    const theme = useTheme();
    const intl = useIntl();
    const [selectedArticles, setSelectedArticles] = useState<number[]>([]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [isPublishAllModalOpen, setIsPublishAllModalOpen] = useState(false);
    const [isAbortModalOpen, setIsAbortModalOpen] = useState(false);
    const [isDeleteArticlesModalOpen, setIsDeleteArticlesModalOpen] = useState(ImportDeleteArticlePublishStatus.None);
    const { importDetails, requesting } = useAppSelector(state => state.import);
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const currentTab = (location.pathname.split('/').pop() as ImportDetailTabs) ?? ImportDetailTabs.Imported;

    const isSelectable =
        currentTab === ImportDetailTabs.Imported &&
        importDetails?.status &&
        [ImportStatus.ReadyToPublish, ImportStatus.Publishing, ImportStatus.Published].includes(importDetails.status);

    const statusMap: Record<ImportDetailTabs, ImportItemStatus[]> = {
        [ImportDetailTabs.Imported]: [ImportItemStatus.NotPublished],
        [ImportDetailTabs.Failed]: [ImportItemStatus.Failed],
        [ImportDetailTabs.Publishing]: [ImportItemStatus.Publishing],
        [ImportDetailTabs.Published]: [ImportItemStatus.Published, ImportItemStatus.Warning],
        [ImportDetailTabs.Warning]: [ImportItemStatus.Warning],
    };

    useEffect(() => {
        if (!importJobId) {
            return;
        }
        dispatch(
            fetchImportJobItems(+importJobId, searchParams, statusMap[currentTab] ?? [ImportItemStatus.NotPublished])
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, searchParams]);

    useEffect(() => {
        if (!importJobId) {
            return;
        }

        const currentPage = Number(searchParams.get('page'));
        searchParams.set('page', '1');
        setSearchParams(searchParams, { replace: true });

        // Workaround
        // because the url-search-params won't changed if already on page 1, no fetch will be executed
        // We could avoid this by putting the current-tab or status also into the url. Maybe in the future
        if (currentPage === 1) {
            dispatch(fetchImportJobItems(+importJobId, searchParams, statusMap[currentTab]));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, currentTab]);

    useEffect(() => {
        if (!importJobId) {
            return;
        }

        dispatch(fetchImportDetails(+importJobId));
    }, [dispatch, importJobId]);

    if (!importJobId) {
        return null;
    }

    const handleDeleteClick = () => {
        setIsDeleteModalOpen(true);
    };

    const handleImportDelete = () => {
        dispatch(deleteImport(+importJobId));
        navigate(ROUTES.products.import.overview.base);
        setIsDeleteModalOpen(false);
    };

    const handleRemoveArticles = () => {
        dispatch(removeArticles(+importJobId, selectedArticles));
        setSelectedArticles([]);
        setIsRemoveModalOpen(false);
    };

    const handlePublishArticlesClick = () => {
        if (importDetails?.shouldDeleteExisting) {
            setIsDeleteArticlesModalOpen(ImportDeleteArticlePublishStatus.Single);
            return;
        }

        handlePublishArticles();
    };

    const handlePublishArticles = () => {
        dispatch(publishArticles(+importJobId, selectedArticles));
        setSelectedArticles([]);

        if (importDetails?.shouldDeleteExisting) {
            setIsDeleteArticlesModalOpen(ImportDeleteArticlePublishStatus.None);
        }
    };

    const handleSelected = (selected: number[]) => {
        setSelectedArticles(selected);
    };

    const handlePublishAllClick = () => {
        if (importDetails?.shouldDeleteExisting) {
            setIsDeleteArticlesModalOpen(ImportDeleteArticlePublishStatus.All);
            return;
        }
        setIsPublishAllModalOpen(true);
    };

    const handleAbortClick = () => {
        setIsAbortModalOpen(true);
    };

    const handlePublishAll = async () => {
        await dispatch(publishAllImports(+importJobId));
        dispatch(fetchImportJobItems(+importJobId, searchParams, statusMap[currentTab]));
        setIsPublishAllModalOpen(false);

        if (importDetails?.shouldDeleteExisting) {
            setIsDeleteArticlesModalOpen(ImportDeleteArticlePublishStatus.None);
        }
    };

    const handleAbortImport = async () => {
        dispatch(abortImport(+importJobId));
        setIsAbortModalOpen(false);
    };

    if (!importDetails) {
        return null;
    }

    const importAllDisabled =
        importDetails.status !== ImportStatus.Published &&
        importDetails.status !== ImportStatus.ReadyToPublish &&
        importDetails.status !== ImportStatus.Scheduled;

    const abortDisabled = importDetails.status !== ImportStatus.Publishing;

    return (
        <>
            <PoolDocumentTitle titleTranslationId={'app.navigation.import'} name={importDetails?.name} />
            <PaloOverview
                dock={
                    <PaloActivityDock
                        defaultOpen={false}
                        panels={[
                            {
                                key: 'details',
                                label: <FormattedMessage id="app.common.details" />,
                                content: <ImportDetailSidebar selectedTab={currentTab} importJobId={+importJobId} />,
                            },
                        ]}
                    />
                }
                requesting={requesting}
                titleMessage={importDetails.name}
                filters={<ImportDetailFilter selectedTab={currentTab} />}
                breadcrumbs={[
                    {
                        label: <FormattedMessage id="app.navigation.import" />,
                        onClick: () => navigate(ROUTES.products.import.overview.all),
                    },
                ]}
                additionalItemsLeft={[
                    <PoolButton
                        startIcon={<TrashIcon />}
                        styleType="toolbar"
                        key="2"
                        onClick={handleDeleteClick}
                        disabled={!isImportDeletable(importDetails.status)}
                    >
                        <FormattedMessage id="app.imports.detail.deleteImport" />
                    </PoolButton>,
                    <PoolButton
                        startIcon={
                            <SelectAllIcon
                                stroke={importAllDisabled ? theme.toolbar.disabledColor : theme.toolbar.color}
                            />
                        }
                        styleType="toolbar"
                        key="3"
                        onClick={handlePublishAllClick}
                        disabled={importAllDisabled}
                    >
                        <FormattedMessage id="app.imports.detail.publishAll" />
                    </PoolButton>,
                    <PoolButton
                        startIcon={
                            <CancelIcon stroke={abortDisabled ? theme.toolbar.disabledColor : theme.toolbar.color} />
                        }
                        styleType="toolbar"
                        key="4"
                        onClick={handleAbortClick}
                        disabled={abortDisabled}
                    >
                        <FormattedMessage id="app.imports.abort" />
                    </PoolButton>,
                ]}
                additionalItemsRight={
                    <div className={classes.statusContainer}>
                        <FormattedMessage id="app.label.status" />
                        <ImportStatusChip
                            className={classes.statusChip}
                            importJob={importDetails}
                            publishDate={
                                importDetails.publishDate
                                    ? intl.formatDate(importDetails.publishDate, {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                      })
                                    : undefined
                            }
                        />
                    </div>
                }
                tabs={
                    <RouteTabs tabs={ImportDetailTabs}>
                        <RouteTab
                            value={ImportDetailTabs.Imported}
                            label={<FormattedMessage id={'app.navigation.imported'} />}
                            badge={
                                <FormattedNumber
                                    value={Math.max(
                                        0,
                                        importDetails.itemCount -
                                            importDetails.publishedItemCount -
                                            importDetails.failedItemCount -
                                            importDetails.publishingItemCount
                                    )}
                                />
                            }
                        />
                        <RouteTab
                            value={ImportDetailTabs.Publishing}
                            label={<FormattedMessage id="app.navigation.publishing" />}
                            badge={<FormattedNumber value={importDetails.publishingItemCount} />}
                        />
                        <RouteTab
                            value={ImportDetailTabs.Published}
                            label={<FormattedMessage id="app.navigation.published" />}
                            badge={<FormattedNumber value={importDetails.publishedItemCount} />}
                        />
                        <RouteTab
                            value={ImportDetailTabs.Failed}
                            label={<FormattedMessage id="app.navigation.failed" />}
                            badge={<FormattedNumber value={importDetails.failedItemCount} />}
                        />
                        <RouteTab
                            value={ImportDetailTabs.Warning}
                            label={<FormattedMessage id="app.navigation.warning" />}
                            badge={<FormattedNumber value={importDetails.warningItemCount} />}
                        />
                    </RouteTabs>
                }
            >
                <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                        <Routes>
                            <Route
                                path={ImportDetailTabs.Imported}
                                element={
                                    <ImportDetailTable
                                        selectedTab={ImportDetailTabs.Imported}
                                        onSelected={handleSelected}
                                        selected={selectedArticles}
                                    />
                                }
                            />
                            <Route
                                path={ImportDetailTabs.Publishing}
                                element={
                                    <ImportDetailTable
                                        selectedTab={ImportDetailTabs.Publishing}
                                        onSelected={handleSelected}
                                        selected={selectedArticles}
                                    />
                                }
                            />
                            <Route
                                path={ImportDetailTabs.Published}
                                element={
                                    <ImportDetailTable
                                        selectedTab={ImportDetailTabs.Published}
                                        onSelected={handleSelected}
                                        selected={selectedArticles}
                                    />
                                }
                            />
                            <Route
                                path={ImportDetailTabs.Warning}
                                element={
                                    <ImportDetailErrorTable field={'warnings'} label={'app.table.column.warning'} />
                                }
                            />
                            <Route
                                path={ImportDetailTabs.Failed}
                                element={<ImportDetailErrorTable field={'errors'} label={'app.table.column.error'} />}
                            />
                            <Route
                                path="*"
                                element={<Navigate to={{ pathname: ImportDetailTabs.Imported }} replace={true} />}
                            />
                        </Routes>

                        {isSelectable && (
                            <>
                                <Divider />
                                <div className={classes.footer}>
                                    <PoolButton
                                        styleType="firstLevel"
                                        style={{ marginLeft: 40 }}
                                        disabled={selectedArticles.length === 0}
                                        onClick={handlePublishArticlesClick}
                                    >
                                        <FormattedMessage id="app.imports.detail.releaseArticle" />
                                    </PoolButton>
                                    <PoolButton
                                        styleType="firstLevel"
                                        color="secondary"
                                        variant="text"
                                        disabled={selectedArticles.length === 0}
                                        onClick={() => setIsRemoveModalOpen(true)}
                                    >
                                        <FormattedMessage id="app.imports.detail.removeArticle" />
                                    </PoolButton>
                                </div>
                            </>
                        )}
                    </Box>
                </Box>
            </PaloOverview>

            {/* Delete Import */}
            <ConfirmationDialog
                isVisible={isDeleteModalOpen}
                title={<FormattedMessage id="app.imports.delete.title" />}
                action={<FormattedMessage id="app.common.delete" />}
                onConfirm={handleImportDelete}
                onCancel={() => setIsDeleteModalOpen(false)}
            >
                <PoolFormattedMessage id="app.imports.delete.content" values={{ name: importDetails.name }} />
            </ConfirmationDialog>

            {/* Remove Single/Multiple Articles */}
            <ConfirmationDialog
                isVisible={isRemoveModalOpen}
                title={<FormattedMessage id="app.imports.detail.removeArticle" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setIsRemoveModalOpen(false)}
                onConfirm={handleRemoveArticles}
            >
                <FormattedMessage id="app.imports.remove.content" />
            </ConfirmationDialog>

            {/* Abort Import */}
            <ConfirmationDialog
                isVisible={isAbortModalOpen}
                title={<FormattedMessage id="app.imports.abort" />}
                action={<FormattedMessage id="app.imports.abortDialog.confirm" />}
                cancelText={<FormattedMessage id="app.imports.abortDialog.cancel" />}
                onCancel={() => setIsAbortModalOpen(false)}
                onConfirm={handleAbortImport}
            >
                <PoolTypography id="app.imports.abort.content" />
            </ConfirmationDialog>

            {/* Publish all imports */}
            <ConfirmationDialog
                isVisible={isPublishAllModalOpen}
                title={<FormattedMessage id="app.imports.detail.publishAll" />}
                action={<FormattedMessage id="app.common.publish" />}
                onCancel={() => setIsPublishAllModalOpen(false)}
                onConfirm={handlePublishAll}
                isSuccessDialog={true}
            >
                <PoolTypography id="app.imports.publishAll.content" />
            </ConfirmationDialog>

            {/* Delete Articles when Publish Single/Multiple Articles */}
            <ConfirmationDialog
                isVisible={isDeleteArticlesModalOpen !== ImportDeleteArticlePublishStatus.None}
                onCancel={() => setIsDeleteArticlesModalOpen(ImportDeleteArticlePublishStatus.None)}
                onConfirm={
                    isDeleteArticlesModalOpen === ImportDeleteArticlePublishStatus.All
                        ? handlePublishAll
                        : handlePublishArticles
                }
                title={<FormattedMessage id="app.imports.detail.deleteExisting" />}
                action={<FormattedMessage id="app.common.confirm" />}
            >
                <PoolFormattedMessage id="app.imports.detail.deleteExistingWarning" />
            </ConfirmationDialog>
        </>
    );
};
